import { Component, Host, Prop, h, Element } from '@stencil/core';
import { ObjectPosition } from '../../../types/global';

type Sizes = 240 | 480 | 640 | 960 | 1200 | 2400;

@Component({
  tag: 'ui-cms-image',
  styleUrl: 'ui-cms-image.scss',
})
export class UiCmsImage {
  @Element() el: HTMLUiCmsImageElement;

  @Prop() alt = '';

  @Prop() src = '';

  /**
   * Source for mobile screen (optional). If provided, this will be used for small screen devices.
   */
  @Prop() mobileSrc?: string;

  /**
   * Define the size that should be used for the image.
   * The following sizes are provided from celum:
   * 240, 480, 640, 960, 1200, 2400
   * The default size is 240.
   */
  @Prop() size: Sizes = 240;

  /**
   * Providing the height of the image is important to reserve the space needed to display the image,
   * reducing or even preventing a layout shift when the image is downloaded and painted to the screen.
   */
  @Prop() height?: number;

  /**
   * Providing the width of the image is important to reserve the space needed to display the image,
   * reducing or even preventing a layout shift when the image is downloaded and painted to the screen.
   */
  @Prop() width?: number;

  @Prop() loading: 'lazy' | 'eager' = 'lazy';

  @Prop() objectPosition: ObjectPosition = 'center center';

  /** value that should be replaced in the src by the correct image size  */
  private replace = '%RENDITION%';

  /** sizes that are provided from celum */
  private availableSizes: Sizes[] = [240, 480, 640, 960, 1200, 2400];

  private constructUrl(src: string, size: Sizes): string {
    return src.replace(this.replace, size.toString());
  }

  /**
   * All sizes below your chosen size (this.size) will be included in the srcset.
   * All sizes above your chosen size (this.size) will be ignored.
   */
  private constructSrcSet(src: string): string {
    return this.availableSizes
      .filter((size) => size <= this.size)
      .map((size) => `${this.constructUrl(src, size)} ${size}w`)
      .join(', ');
  }

  render() {
    return (
      <Host>
        <picture>
          {this.mobileSrc && <source srcSet={this.constructSrcSet(this.mobileSrc)} media="(max-width: 600px)" />}
          <img
            src={this.constructUrl(this.src, this.size)}
            srcSet={this.constructSrcSet(this.src)}
            alt={this.alt}
            loading={this.loading}
            height={this.height}
            width={this.width}
            style={{ objectPosition: this.objectPosition }}
          />
        </picture>
      </Host>
    );
  }
}
